var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { hasValue, textSearch } from "@utils";
/**
 * @export
 */
export function generateTreeNodesFromFlatList(list, query, idField, labelField, parentIdField, pathField, generatePath, options, iconField) {
    const hasCustomLabel = ((options === null || options === void 0 ? void 0 : options.customLabel) && typeof (options === null || options === void 0 ? void 0 : options.customLabel) == 'function');
    function getCustomLabel(item) {
        if (hasCustomLabel)
            return (options === null || options === void 0 ? void 0 : options.customLabel(item)) || null;
        return null;
    }
    function getParent(id) {
        return list.find(node => node[idField] === id) || null;
    }
    function isNodeDisabled(item, parentDisabled) {
        const disabled = ((options === null || options === void 0 ? void 0 : options.disableNode) && options.disableNode(item)) ||
            (parentDisabled && !((options === null || options === void 0 ? void 0 : options.enableNode) && options.enableNode(item)));
        return disabled;
    }
    function isChildrenDisabled(item, parentChildrenDisabled) {
        const disabled = ((options === null || options === void 0 ? void 0 : options.disableChildNodes) && options.disableChildNodes(item)) ||
            (parentChildrenDisabled && !((options === null || options === void 0 ? void 0 : options.enableChildNode) && options.enableChildNode(item)));
        return disabled;
    }
    function canAddChildren(item) {
        if ((options === null || options === void 0 ? void 0 : options.enableAddChildren) && typeof options.enableAddChildren === 'function') {
            return options.enableAddChildren(item);
        }
        return undefined;
    }
    function canEditNode(item) {
        if ((options === null || options === void 0 ? void 0 : options.enableEditNode) && typeof options.enableEditNode === 'function') {
            return options.enableEditNode(item);
        }
        return undefined;
    }
    function canDeleteNode(item) {
        if ((options === null || options === void 0 ? void 0 : options.enableDeleteNode) && typeof options.enableDeleteNode === 'function') {
            return options.enableDeleteNode(item);
        }
        return undefined;
    }
    function createNodeChildrenMap(list, parentIdField) {
        const nodes = {};
        for (const node of list) {
            const _parent = node[parentIdField];
            if (hasValue(_parent)) {
                if (!nodes[_parent])
                    nodes[_parent] = [];
                nodes[_parent].push(node);
            }
            else {
                if (!nodes['__root__'])
                    nodes['__root__'] = [];
                nodes['__root__'].push(node);
            }
        }
        return nodes;
    }
    function generateNodePath(item, parentPath) {
        const path = (generatePath && hasValue(parentPath)) ? `${parentPath}/${item[pathField]}` : item[pathField];
        if ((options === null || options === void 0 ? void 0 : options.leadingSlash) && !path.startsWith('/'))
            return '/' + path;
        return path;
    }
    function generateNodeDisplayPath(item, parentPath) {
        var _a, _b, _c, _d;
        const itemPathField = hasValue((_a = options === null || options === void 0 ? void 0 : options.displayPath) === null || _a === void 0 ? void 0 : _a.pathField) ? (_b = options === null || options === void 0 ? void 0 : options.displayPath) === null || _b === void 0 ? void 0 : _b.pathField : pathField;
        const path = (((_c = options === null || options === void 0 ? void 0 : options.displayPath) === null || _c === void 0 ? void 0 : _c.generate) && hasValue(parentPath)) ? `${parentPath}/${item[itemPathField]}` : item[itemPathField];
        if (((_d = options === null || options === void 0 ? void 0 : options.displayPath) === null || _d === void 0 ? void 0 : _d.leadingSlash) && !path.startsWith('/'))
            return '/' + path;
        return path;
    }
    function getChildren(nodeId, parentPath, parentDisplayPath, nodesMap, parentDisabled, parentChildrenDisabled, ancestorIds) {
        const children = (nodesMap[nodeId] || []).map((c) => {
            return generateNode(c, parentPath, parentDisplayPath, nodesMap, parentDisabled, parentChildrenDisabled, ancestorIds);
        });
        return children;
    }
    function generateNode(item, parentPath, parentDisplayPath, nodesMap, parentDisabled, parentChildrenDisabled, ancestorIds = new Set()) {
        var _a;
        const id = item[idField];
        const customLabelResult = hasCustomLabel ? getCustomLabel(item) : null;
        // Determine label and renderLabel based on customLabel result
        let label;
        let renderLabel;
        if (customLabelResult !== null) {
            if (typeof customLabelResult === 'string') {
                // String result: use as label directly
                label = customLabelResult;
            }
            else {
                // ReactNode result: use fallback for label, set renderLabel
                label = item[labelField] || 'Node';
                renderLabel = () => customLabelResult;
            }
        }
        else {
            // No custom label: use default field
            label = item[labelField];
        }
        const hasChildren = !!nodesMap[id];
        const path = generateNodePath(item, parentPath);
        const displayPath = ((_a = options === null || options === void 0 ? void 0 : options.displayPath) === null || _a === void 0 ? void 0 : _a.generate) ? generateNodeDisplayPath(item, parentDisplayPath) : path;
        const nodeDisabled = isNodeDisabled(item, parentDisabled);
        const childrenDisabled = isChildrenDisabled(item, parentChildrenDisabled);
        // Cycle guard: if this node is already an ancestor in the current path, treat as leaf
        const hasCycle = ancestorIds.has(id);
        const nextAncestors = hasCycle ? ancestorIds : new Set(ancestorIds).add(id);
        if (hasCycle)
            console.warn(`[treeview] Circular parent reference detected for node "${id}" — skipping children to prevent infinite recursion`);
        const children = (hasChildren && !hasCycle) ? getChildren(id, path, displayPath, nodesMap, nodeDisabled, childrenDisabled, nextAncestors) : [];
        const icon = iconField ? item[iconField] : undefined;
        const enableNodeValue = (options === null || options === void 0 ? void 0 : options.enableNode) ? options.enableNode(item) : undefined;
        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ id,
            label }, (renderLabel && { renderLabel })), (icon && { icon })), { path: path, displayPath, getChildren: children, disableNode: nodeDisabled }), (enableNodeValue !== undefined && { enableNode: enableNodeValue })), { disableChildNodes: childrenDisabled, enableAddChildren: canAddChildren(item), enableEditNode: canEditNode(item), enableDeleteNode: canDeleteNode(item), original: item });
    }
    function getFilteredItems() {
        if (hasValue(query)) {
            // When there's a query, filter nodes
            const matchingNodes = list.filter((node) => {
                if (hasCustomLabel) {
                    const customLabelResult = getCustomLabel(node);
                    // Only search in string labels, skip ReactNode labels (use fallback)
                    const searchString = typeof customLabelResult === 'string'
                        ? customLabelResult
                        : node[labelField] || '';
                    return textSearch(searchString, query, true);
                }
                return textSearch(node[labelField], query, true);
            });
            const ids = matchingNodes.map(n => n[idField]);
            // Backtrack to include parent nodes
            for (const node of matchingNodes) {
                let parent = getParent(node[parentIdField]);
                while (parent != null && !ids.includes(parent[idField])) {
                    ids.push(parent[idField]);
                    matchingNodes.push(parent);
                    parent = getParent(parent[parentIdField]);
                }
            }
            return matchingNodes;
        }
        // if query is empty return the whole list 
        return list;
    }
    const filteredItems = getFilteredItems();
    const nodesMap = createNodeChildrenMap(filteredItems, parentIdField);
    const treeNodes = (nodesMap['__root__'] || []).map((value) => {
        return generateNode(value, '', '', nodesMap, false, false, new Set());
    });
    return treeNodes || [];
}
function getChildren(node, shouldExpand) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!node || !(node === null || node === void 0 ? void 0 : node.getChildren))
            return [];
        // If getChildren is a function and expandOnLoad is true, load children
        if (typeof node.getChildren === 'function' && shouldExpand) {
            try {
                const result = yield node.getChildren(node);
                return result.nodes;
            }
            catch (e) {
                console.error(`Unable to load children for node ${node.id}. Error: `, e);
            }
        }
        else if (Array.isArray(node.getChildren)) {
            // If getChildren is an array, use it as children
            return node.getChildren || [];
        }
        return [];
    });
}
/**
 *
 * @export
 */
export function flattenTreeNodesToOriginalFlatList(treeNodes) {
    return __awaiter(this, void 0, void 0, function* () {
        const flatList = [];
        function flattenNode(node) {
            return __awaiter(this, void 0, void 0, function* () {
                // Add current node's original data to the flat list
                flatList.push(node.original);
                // If node has children, recursively flatten them
                const children = yield getChildren(node, true);
                for (const child of children) {
                    flattenNode(child);
                }
            });
        }
        for (const node of treeNodes) {
            yield flattenNode(node);
        }
        return flatList;
    });
}
/**
 * @export
 */
export function findTreeNodeById(treeNodes, id) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const node of treeNodes) {
            if (node.id === id) {
                // If the node with the given id is found, return it
                return node;
            }
            // If node has children, search within them recursively
            const children = yield getChildren(node, true);
            if ((children === null || children === void 0 ? void 0 : children.length) > 0) {
                const found = yield findTreeNodeById(children, id);
                if (found)
                    return found;
            }
        }
        return null;
    });
}
/**
 * @export
 */
export function findTreeNodeByIdSync(nodes, nodeId) {
    if (!nodes || (nodes === null || nodes === void 0 ? void 0 : nodes.length) == 0)
        return undefined;
    for (const node of nodes) {
        if (node.id === nodeId) {
            return node;
        }
        else if (node.children && node.children.length > 0) {
            const found = findTreeNodeByIdSync(node.children, nodeId);
            if (found)
                return found;
        }
    }
    return undefined;
}
/**
 * @export
 */
export function findTreeNodeByPath(treeNodes, path) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const node of treeNodes) {
            if (node.path === path) {
                // If the node with the given path is found, return it
                return node;
            }
            // If node has children, search within them recursively
            const children = yield getChildren(node, true);
            if ((children === null || children === void 0 ? void 0 : children.length) > 0) {
                const found = yield findTreeNodeByPath(children, path);
                if (found)
                    return found;
            }
        }
        return null;
    });
}
/**
 * extract treeview styles
 */
export function extractTreeViewStyles(props, context) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
    const nodeSize = 28; // expand/collapse button size
    const nodeCenter = nodeSize / 2; // center point of the node button
    const indent = 24; // indentation for child levels
    const pathWidth = 1; // width of connecting lines
    const connectorOffset = 16; // vertical position to align with node center (reduced for compact spacing)
    const horizontalConnectorLength = indent; // distance from vertical line to node
    const childrenMarginLeft = nodeCenter; // children offset to align with parent center
    const treeViewStyles = {
        '--treeViewNodeBGColor': hasValue((_a = props === null || props === void 0 ? void 0 : props.styles) === null || _a === void 0 ? void 0 : _a.nodeBGColor) ? (_b = props === null || props === void 0 ? void 0 : props.styles) === null || _b === void 0 ? void 0 : _b.nodeBGColor : context.theme.primaryBGColor,
        '--treeViewNodeIconColor': hasValue((_c = props === null || props === void 0 ? void 0 : props.styles) === null || _c === void 0 ? void 0 : _c.nodeIconColor) ? (_d = props === null || props === void 0 ? void 0 : props.styles) === null || _d === void 0 ? void 0 : _d.nodeIconColor : context.theme.primaryTextColor,
        '--treeViewNodeBorderColor': hasValue((_e = props === null || props === void 0 ? void 0 : props.styles) === null || _e === void 0 ? void 0 : _e.nodeBorderColor) ? (_f = props === null || props === void 0 ? void 0 : props.styles) === null || _f === void 0 ? void 0 : _f.nodeBorderColor : context.theme.primaryTextColor,
        '--treeViewPathColor': hasValue((_g = props === null || props === void 0 ? void 0 : props.styles) === null || _g === void 0 ? void 0 : _g.pathColor) ? (_h = props === null || props === void 0 ? void 0 : props.styles) === null || _h === void 0 ? void 0 : _h.pathColor : 'rgba(0, 0, 0, 0.1)',
        '--treeViewLabelBGColor': hasValue((_j = props === null || props === void 0 ? void 0 : props.styles) === null || _j === void 0 ? void 0 : _j.labelBGColor) ? (_k = props === null || props === void 0 ? void 0 : props.styles) === null || _k === void 0 ? void 0 : _k.labelBGColor : context.theme.primaryBGColor,
        '--treeViewLabelTextColor': hasValue((_l = props === null || props === void 0 ? void 0 : props.styles) === null || _l === void 0 ? void 0 : _l.labelTextColor) ? (_m = props === null || props === void 0 ? void 0 : props.styles) === null || _m === void 0 ? void 0 : _m.labelTextColor : context.theme.primaryTextColor,
        '--treeViewLabelBorderRadius': hasValue((_o = props === null || props === void 0 ? void 0 : props.styles) === null || _o === void 0 ? void 0 : _o.labelBorderRadius) ? `${(_p = props === null || props === void 0 ? void 0 : props.styles) === null || _p === void 0 ? void 0 : _p.labelBorderRadius}px` : '4px',
        '--treeViewLabelSelectedBGColor': hasValue((_q = props === null || props === void 0 ? void 0 : props.styles) === null || _q === void 0 ? void 0 : _q.labelSelectedBGColor) ? (_r = props === null || props === void 0 ? void 0 : props.styles) === null || _r === void 0 ? void 0 : _r.labelSelectedBGColor : context.theme.selectedBGColor,
        '--treeViewLabelSelectedTextColor': hasValue((_s = props === null || props === void 0 ? void 0 : props.styles) === null || _s === void 0 ? void 0 : _s.labelSelectedTextColor) ? (_t = props === null || props === void 0 ? void 0 : props.styles) === null || _t === void 0 ? void 0 : _t.labelSelectedTextColor : context.theme.selectedTextColor,
        '--treeViewLabelHoverBGColor': '#DADADA22',
        '--treeViewLabelHoverTextColor': hasValue((_u = props === null || props === void 0 ? void 0 : props.styles) === null || _u === void 0 ? void 0 : _u.labelTextColor) ? (_v = props === null || props === void 0 ? void 0 : props.styles) === null || _v === void 0 ? void 0 : _v.labelTextColor : context.theme.primaryTextColor,
        '--treeViewDisabledBGColor': hasValue((_w = props === null || props === void 0 ? void 0 : props.styles) === null || _w === void 0 ? void 0 : _w.disabledBGColor) ? (_x = props === null || props === void 0 ? void 0 : props.styles) === null || _x === void 0 ? void 0 : _x.disabledBGColor : context.theme.secondaryBGColor,
        '--treeViewDisabledTextColor': hasValue((_y = props === null || props === void 0 ? void 0 : props.styles) === null || _y === void 0 ? void 0 : _y.disabledTextColor) ? (_z = props === null || props === void 0 ? void 0 : props.styles) === null || _z === void 0 ? void 0 : _z.disabledTextColor : context.theme.secondaryTextColor,
        // Path drawing variables - all calculated in JS
        '--treeViewNodeSize': `${nodeSize}px`,
        '--treeViewNodeCenter': `${nodeCenter}px`,
        '--treeViewIndent': `${indent}px`,
        '--treeViewPathWidth': `${pathWidth}px`,
        '--treeViewConnectorOffset': `${connectorOffset}px`,
        '--treeViewHorizontalConnectorLength': `${horizontalConnectorLength}px`,
        '--treeViewChildrenMarginLeft': `${childrenMarginLeft}px`,
    };
    return treeViewStyles;
}
/**
 * @export
 * Process a TreeNode to ExtendedTreeNode
 */
export function processTreeNode(nodes, node, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const existingNode = findTreeNodeByIdSync(nodes, node.id);
        // If the node existed before, preserve the user's expand/collapse choice.
        // Only apply expandAllNodes to nodes that are new (not yet in the tree).
        const shouldExpand = ((node === null || node === void 0 ? void 0 : node.expandOnLoad) !== false) && (existingNode
            ? existingNode.expanded
            : (node.expandOnLoad || (options === null || options === void 0 ? void 0 : options.expandAllNodes)));
        const children = yield getChildren(node, shouldExpand);
        const nodePath = (hasValue(options === null || options === void 0 ? void 0 : options.parentPath)) ? [(options === null || options === void 0 ? void 0 : options.parentPath) || '', node.label].join('/') : node.label;
        const processedChildren = yield Promise.all(children.map(c => processTreeNode(nodes, c, { parentId: node.id, parentPath: nodePath, expandAllNodes: options === null || options === void 0 ? void 0 : options.expandAllNodes })));
        return Object.assign(Object.assign({}, node), { children: processedChildren, expanded: shouldExpand && (processedChildren === null || processedChildren === void 0 ? void 0 : processedChildren.length) > 0, expanding: false, parentId: options === null || options === void 0 ? void 0 : options.parentId, parentPath: options === null || options === void 0 ? void 0 : options.parentPath, path: (!hasValue(node.path)) ? nodePath : node.path });
    });
}
/**
 * Process a TreeNodes to ExtendedTreeNodes
 */
export function processAllTreeNodes(processedNodes, nodes, expandAllNodes) {
    return __awaiter(this, void 0, void 0, function* () {
        const existingProcessedNodes = (processedNodes === null || processedNodes === void 0 ? void 0 : processedNodes.length) > 0 ? processedNodes : [];
        const newlyProcessedNodes = yield Promise.all(nodes.map(node => processTreeNode(existingProcessedNodes, node, { expandAllNodes })));
        return newlyProcessedNodes;
    });
}
/**
 * Calculate drop position based on mouse cursor position relative to node-head
 * Uses real mouse Y coordinate for accurate, scroll-aware positioning
 *
 * Node-head is always at top of container (children are in node-body below)
 *
 * For reordering (same parent):
 * - Top 50%: 'before' (insert as sibling above)
 * - Bottom 50%: 'after' (insert as sibling below)
 *
 * For hierarchy changes (different parent):
 * - Top 30%: 'before' (insert as sibling above)
 * - Middle 30-60%: 'child' (add as last child)
 * - Bottom 40%: 'after' (insert as sibling below)
 *
 * @export
 * @param mouseY - Mouse Y coordinate (clientY from pointer event)
 * @param targetRect - Rectangle of the target node container (from over.rect)
 * @param isReordering - Whether this is a reordering operation (same parent) vs hierarchy change
 * @returns DropPosition or null if mouse is outside node-head area
 */
export function calculateDropPosition(mouseY, targetRect, isReordering) {
    // Node-head height is fixed in CSS (see _tree_view.scss)
    const NODE_HEAD_HEIGHT = 40;
    // Node-head is always at top of container
    const nodeHeadTop = targetRect.top;
    const nodeHeadBottom = targetRect.top + NODE_HEAD_HEIGHT;
    // Check if mouse is within node-head area
    // This prevents showing indicators when hovering over expanded children
    if (mouseY < nodeHeadTop || mouseY > nodeHeadBottom) {
        return null; // Mouse outside node-head
    }
    // Calculate relative position within node-head (0 to 1)
    const relativePosition = (mouseY - nodeHeadTop) / NODE_HEAD_HEIGHT;
    // For reordering: simple 50%/50% split (no child option)
    if (isReordering) {
        return relativePosition < 0.5 ? 'before' : 'after';
    }
    // For hierarchy changes: 30%/40%/30% split with child option
    // Top 30% = before
    if (relativePosition < 0.3) {
        return 'before';
    }
    // Bottom 60% = after
    else if (relativePosition > 0.6) {
        return 'after';
    }
    // Middle 30-60% = child
    else {
        return 'child';
    }
}
export function generateIdWithPrefix(id, prefix) {
    if (hasValue(prefix))
        return `${prefix}___${id}`;
    return id;
}
/**
 * Parse a potentially prefixed node ID using regex
 * Returns tree identifier and clean node ID in one operation
 *
 * @export
 * @param id - Either prefixed (e.g., "left___node-1") or unprefixed (e.g., "node-1")
 * @returns {tree: 'left' | 'right' | null, nodeId: string}
 *
 * @example
 * parsePrefixedId("left___folder-1")   // { tree: 'left', nodeId: 'folder-1' }
 * parsePrefixedId("right___item-5")    // { tree: 'right', nodeId: 'item-5' }
 * parsePrefixedId("unprefixed-node")   // { tree: null, nodeId: 'unprefixed-node' }
 */
export function parsePrefixedId(id) {
    if (!id)
        return { tree: null, nodeId: '' };
    const match = id.match(/^(left|right)___(.+)$/);
    if (match) {
        return {
            tree: match[1],
            nodeId: match[2]
        };
    }
    return { tree: null, nodeId: id };
}
